import { globalLoggerToken } from "@/tokens";
import ApiClient from "@lib/ApiClient";
import FetchAndXHRPatcher from "@lib/FetchAndXHRPatcher/FetchAndXHRPatcher";
import { SDK_MODULES } from "@modules/sdk/constants";
import { SdkModule } from "@modules/sdk/SdkModule";
import { container, instanceCachingFactory, singleton } from "tsyringe";

import { ModuleInterface } from "@interfaces/ModuleInterface";

import { AjaxApiCartApiService } from "./api_services/AjaxApiCartApiService/AjaxApiCartApiService";
import { CartSdk } from "./cart_sdk/CartSdk";
import { CartQuery } from "./queries/CartQuery";
import { AjaxApiRepository } from "./repositories/AjaxApiRepository";
import { CartRepositoryProvider } from "./services/CartRepositoryProvider";
import { CartService } from "./services/CartService";
import { CartStore } from "./stores/CartStore";
import {
    cartManagerCounterUpdatersContextToken,
    cartManagerItemsUpdatersContextToken,
    cartQueryToken,
    cartServiceToken,
    cartStoreToken,
    fetchAndXhrPatcherToken,
} from "./tokens";
import CartListener from "./utils/CartListener/CartListener";
import FetchOrXHRPatcherListenerStrategy from "./utils/CartListener/FetchOrXHRPatcherListenerStrategy";
import SendFormListenerStrategy from "./utils/CartListener/SendFormListenerStrategy";
import CartManagerCounterUpdatersContext from "./utils/CartManager/CartManagerCounterUpdatersContext";
import CartManagerItemsUpdatersContext from "./utils/CartManager/CartManagerItemsUpdatersContext";

@singleton()
export class CartModule
    implements
        ModuleInterface<{
            cartListener: CartListener;
            cartService: CartService;
            cartQuery: CartQuery;
        }>
{
    exports: {
        cartListener: CartListener;
        cartService: CartService;
        cartQuery: CartQuery;
    };

    public constructor() {
        const cartModuleLogger = container
            .resolve(globalLoggerToken)
            .newLogger("CartModule");

        // (utils) fetchAndXHRPatcher
        container.register(fetchAndXhrPatcherToken, {
            useFactory: instanceCachingFactory(() => {
                const fetchAndXHRPatcher = new FetchAndXHRPatcher(
                    container.resolve(globalLoggerToken)
                );
                fetchAndXHRPatcher.patch();
                return fetchAndXHRPatcher;
            }),
        });
        const fetchAndXHRPatcher = container.resolve(fetchAndXhrPatcherToken);

        container.register(cartManagerCounterUpdatersContextToken, {
            useFactory: instanceCachingFactory(() => {
                return new CartManagerCounterUpdatersContext();
            }),
        });

        container.register(cartManagerItemsUpdatersContextToken, {
            useFactory: instanceCachingFactory(() => {
                return new CartManagerItemsUpdatersContext();
            }),
        });

        container.register(cartStoreToken, {
            useFactory: instanceCachingFactory(() => new CartStore()),
        });

        const cartRepositoryProvider = new CartRepositoryProvider(
            new AjaxApiRepository(
                new AjaxApiCartApiService(new ApiClient("/"), cartModuleLogger)
            )
        );

        container.register(cartServiceToken, {
            useFactory: instanceCachingFactory((di) => {
                return new CartService(
                    cartRepositoryProvider,
                    cartModuleLogger,
                    di.resolve(cartManagerCounterUpdatersContextToken),
                    di.resolve(cartManagerItemsUpdatersContextToken),
                    di.resolve(cartStoreToken)
                );
            }),
        });

        container.register(cartQueryToken, {
            useFactory: instanceCachingFactory((di) => {
                return new CartQuery(di.resolve(cartStoreToken));
            }),
        });

        const cartSdk = new CartSdk(cartRepositoryProvider);
        container
            .resolve(SdkModule)
            .exports.sdk.registerModule(SDK_MODULES.CART, cartSdk);

        // (utils) cartListener
        const cartListener = new CartListener(cartModuleLogger);
        cartListener.register(new SendFormListenerStrategy(cartModuleLogger));
        cartListener.register(
            new FetchOrXHRPatcherListenerStrategy(
                cartModuleLogger,
                fetchAndXHRPatcher
            )
        );

        this.exports = {
            cartListener,
            cartQuery: container.resolve(cartQueryToken),
            cartService: container.resolve(cartServiceToken),
        };
    }

    registerProviders() {
        return {};
    }
}
